import {Link} from '@/i18n/navigation';
import GameCard from "@/app/components/game/game-card/GameCard";
import {SnapCarousel} from "@/app/components/hero/snap-carousel/SnapCarousel";
import React from "react";
import ProviderCard from "@/app/components/game/ProviderCard";
import {IGame} from "@/lib/types/games/IGame";
import {IProducer} from "@/lib/types/games/IProducer";
import {ILobbyCategory} from "@/lib/types/games/ILobbyCategory";
import {LobbyCategoryTypeEnum} from "@/lib/enums/LobbyCategoryTypeEnum";
import {categoryIconName, categoryLabelKey} from "@/lib/utils/contants/lobbyCategories";
import {Icon} from "@/app/components/common/Icon";
import {useAppSelector} from "@/lib/hooks";
import {AuthTypesEnum} from "@/lib/enums/AuthTypesEnum";
import {useTranslations} from "next-intl";
import {Skeleton} from "@/app/components/common/app-skeleton/AppSkeleton";
import {useOpenDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {rememberGameOpenSource} from "@/lib/analytics/mixpanel-events";

interface IProps {
    // AC-819: the lobby structure (rows and their order) is resolved by the
    // backend for the player's GEO; this component just renders it.
    categories: ILobbyCategory[];
    games: Record<string, IGame[]>;
    producers: IProducer[];
    fetching: boolean;
}

export const LobbyTab = ({categories, games, producers, fetching}: IProps) => {
    const isAuthorized = useAppSelector(state => !!state.auth.userId);
    const userCountry = useAppSelector(state => state.auth.country);
    const t = useTranslations();
    const openDialog = useOpenDialog();

    const isCardGated = (item: IGame) => !isAuthorized || item.producer.restrictedCountries.includes(userCountry);

    const onCardClick = (event: React.MouseEvent<HTMLAnchorElement>, item: IGame) => {
        if (!isCardGated(item)) {
            // The game page emits `game_opened`; it can only know which grid the
            // player came from if the grid says so on its way out.
            rememberGameOpenSource("casino");
            return;
        }

        event.preventDefault();

        if (!isAuthorized) {
            openDialog(DialogsEnum.AUTH, {props: {tab: AuthTypesEnum.REGISTER}});
        }
    }

    // Known slugs keep their localized labels; backoffice-created categories
    // fall back to the title configured there.
    const rowTitle = (category: ILobbyCategory) => {
        const labelKey = categoryLabelKey(category.slug);
        return t.has(labelKey) ? t(labelKey) : category.title;
    };

    return (
        <>
            {categories.map((category) => {
                if (category.type === LobbyCategoryTypeEnum.PROVIDERS) {
                    if (!fetching && !producers.length) {
                        return null;
                    }
                    return (
                        <SnapCarousel key={category.id} title={rowTitle(category)}
                                      icon={<Icon name={categoryIconName(category.slug)}/>}
                                      items={fetching ? Array.from({length: 20}).map(() => null) : producers}
                                      href={'/casino/providers'}>
                            {(item) => (
                                <div
                                    className={'relative shrink-0 aspect-video w-[calc((100vw-16px*3)/3)] sm:w-[calc((100vw-16px*5)/5)] md:w-[calc((100vw-16px*6)/6)] lg:w-[calc((100vw-16px*7)/7)] xl:w-[calc((100vw-27px*8)/8)] 2xl:w-[calc((100vw-16px*8)/8)]'}>
                                    {
                                        !item ? (
                                            <Skeleton className="absolute inset-0 bg-noble-black-600"/>
                                        ) : (
                                            <Link prefetch={false}
                                                  href={`/casino/providers/${item.title.toLowerCase().replaceAll(" ", "-")}`}>
                                                <ProviderCard
                                                    sizes="(max-width: 640px) 76px,
                                                      (max-width: 768px) 76px,
                                                      (max-width: 1024px) 89px,
                                                      (max-width: 1280px) 91px,
                                                      (max-width: 1536px) 121px,
                                                       210px"
                                                    poster={item.previewUrl} name={item.title}
                                                />
                                            </Link>
                                        )
                                    }
                                </div>
                            )}
                        </SnapCarousel>
                    );
                }

                const rowGames = games[category.slug] || [];
                if (!fetching && !rowGames.length) {
                    return null;
                }
                return (
                    <SnapCarousel key={category.id} title={rowTitle(category)}
                                  icon={<Icon name={categoryIconName(category.slug)}/>}
                                  items={fetching ? Array.from({length: 20}).map(() => null) : rowGames}
                                  href={`/casino/categories/${category.slug}`}>
                        {(item) => (
                            <div
                                className={'relative shrink-0 aspect-3/4 w-[calc((100vw-16px*3)/3)] sm:w-[calc((100vw-16px*5)/5)] md:w-[calc((100vw-16px*6)/6)] lg:w-[calc((100vw-16px*7)/7)] xl:w-[calc((100vw-27px*8)/8)] 2xl:w-[calc((100vw-16px*9)/9)]'}>
                                {
                                    !item ? (
                                        <Skeleton className="absolute inset-0 bg-noble-black-600"/>
                                    ) : (
                                        <Link
                                            href={isCardGated(item) ? '#' : `/games/${item.id}`}
                                            onClick={(event) => onCardClick(event, item)}>
                                            <GameCard
                                                isUnavailable={item.producer.restrictedCountries.includes(userCountry)}
                                                poster={item.previewUrl}
                                                sizes="(max-width: 640px) 112px,
                                                  (max-width: 768px) 112px,
                                                  (max-width: 1024px) 131px,
                                                  (max-width: 1280px) 133px,
                                                  (max-width: 1536px) 155px,
                                                   270px"
                                                gameName={item.title}
                                                providerName={item.producer.title}
                                            />
                                        </Link>
                                    )
                                }
                            </div>
                        )}
                    </SnapCarousel>
                );
            })}
        </>
    )
}
